export const PODO_BACKEND_COMPAT_RELEASE = 'v06-web-compat'

const RELEASE_HEADER = 'X-Podo-Release-Lane'
const COMPAT_AUTH_PATHS = new Set([
  '/api/v1/auth/system/exchange',
  '/api/v1/auth/internal/access-token',
  '/api/v1/auth/introspect',
  '/api/v1/auth/refresh',
])

interface PodoBackendRequest {
  url: string
  method?: string
  headers?: Record<string, string>
  publicBackendUrl: string
  /** Omit this entirely in browser execution; values come only from private runtime configuration. */
  server?: { backendUrl: string; release?: typeof PODO_BACKEND_COMPAT_RELEASE }
}

/** Routing metadata never comes from the user and confers no authentication authority. */
export function preparePodoBackendRequest({ url, method, headers, publicBackendUrl, server }: PodoBackendRequest) {
  const requestHeaders = Object.fromEntries(
    Object.entries(headers ?? {}).filter(([name]) => name.toLowerCase() !== RELEASE_HEADER.toLowerCase()),
  )
  const isPost = (method ?? 'GET').toUpperCase() === 'POST'
  const absoluteUrl = URL.canParse(url) ? new URL(url) : undefined
  const isAbsoluteOwnedLogout =
    isPost &&
    absoluteUrl?.protocol === 'https:' &&
    absoluteUrl.pathname === '/api/v1/auth/logout' &&
    !absoluteUrl.username &&
    !absoluteUrl.password &&
    !absoluteUrl.search &&
    !absoluteUrl.hash
  if (server?.release === undefined) {
    const requestUrl = new URL(url, publicBackendUrl)
    // Owned cleanup must never forward the refresh body through an upstream redirect.
    const isIssuerLogout =
      isAbsoluteOwnedLogout &&
      server &&
      URL.canParse(server.backendUrl) &&
      requestUrl.origin === new URL(server.backendUrl).origin
    return {
      url: requestUrl,
      headers: requestHeaders,
      ...(isIssuerLogout ? { redirect: 'error' as const } : {}),
    }
  }

  let backend: URL
  try {
    backend = new URL(server.backendUrl)
    if (
      server.release !== PODO_BACKEND_COMPAT_RELEASE ||
      backend.protocol !== 'https:' ||
      backend.username ||
      backend.password ||
      backend.search ||
      backend.hash
    ) {
      throw new Error()
    }
  } catch {
    // Do not include runtime URLs or supplied credentials in an error.
    throw new Error('Invalid backend release configuration')
  }

  // Normal relative logout retains its canonical store; only owned issuer cleanup is absolute.
  const isRelativeAuth = isPost && COMPAT_AUTH_PATHS.has(url)
  const requestUrl = isRelativeAuth
    ? new URL(url, backend)
    : URL.canParse(url)
      ? new URL(url)
      : new URL(url, publicBackendUrl)
  if (
    isPost &&
    requestUrl.origin === backend.origin &&
    !requestUrl.username &&
    !requestUrl.password &&
    !requestUrl.search &&
    !requestUrl.hash &&
    (COMPAT_AUTH_PATHS.has(requestUrl.pathname) || isAbsoluteOwnedLogout)
  ) {
    requestHeaders[RELEASE_HEADER] = PODO_BACKEND_COMPAT_RELEASE
    return { url: requestUrl, headers: requestHeaders, redirect: 'error' as const }
  }
  return { url: requestUrl, headers: requestHeaders }
}
